import { Body, Button, ButtonGroup, Column, InlineAlert, LgPadding } from '@alto/design-system';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
// eslint-disable-next-line import/no-deprecated
import { clearInsuranceError, createPhotoInsurance } from '~shared/actions/insurances';
import { updateCashPay } from '~shared/actions/users';
import { DEFAULT_ERROR_ALERT_MESSAGE, DEFAULT_ERROR_ALERT_TITLE, type InsuranceSource } from '~shared/constants';
import { getSelectedUserID } from '~shared/features/ui/selectors/getUsers';
import { getCurrentUserID } from '~shared/features/users/selectors/getUsers';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { getInsuranceSource } from '~shared/lib/analytics/src/getInsuranceSource';
import { queries } from '~shared/queries/query-keys';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { InsurancePhotoUploadButtons } from './InsurancePhotoUploadButtons';

const initialValues = {
  image_url: [] as File[],
  image_url_2: [] as File[],
};

type Props = {
  readonly onCancel: () => void;
  readonly onSave: () => void;
  readonly source?: InsuranceSource;
};

export const InsurancePhotoForm = ({ onCancel, onSave, source: sourceProp }: Props) => {
  const [frontPhotoError, setFrontPhotoError] = useState('');
  const [backPhotoError, setBackPhotoError] = useState('');
  const queryClient = useQueryClient();
  const dispatch = useDispatchShared();
  const { trackEvent } = useAnalytics();
  const currentUserID = useSelectorShared(getCurrentUserID);
  const selectedUserID = useSelectorShared(getSelectedUserID);
  const userID = selectedUserID || currentUserID;
  const insuranceError = useSelectorShared(
    (state) =>
      state.ui.errors.fetchInsurancesError ||
      state.ui.errors.createInsuranceError ||
      state.ui.errors.updateInsuranceError ||
      state.ui.errors.createPhotoInsuranceError,
  );
  const loading = useSelectorShared((state) => state.ui.loading.createPhotoInsuranceLoading);
  const source = useSelectorShared(getInsuranceSource) ?? sourceProp;

  const onSubmit = async (insurance: typeof initialValues) => {
    if (insurance.image_url.length === 0) {
      setFrontPhotoError('Please add a photo for the front of your benefits card.');
      return;
    }
    if (insurance.image_url_2.length === 0) {
      setBackPhotoError('Please add a photo for the back of your benefits card.');
      return;
    }

    dispatch(clearInsuranceError());

    const images = [insurance.image_url[0], insurance.image_url_2[0]];
    if (images.length > 0) {
      // eslint-disable-next-line import/no-deprecated
      const insurance = await dispatch(createPhotoInsurance(images, userID));
      if (typeof insurance !== 'boolean') {
        trackEvent({
          event: EVENTS.INSURANCE_ADDED,
          params: {
            type: 'photo',
            source,
            entity_name: insurance.insurance_plan_name ?? '',
          },
        });
        queryClient.invalidateQueries({ queryKey: queries.insurances.fetchAll._def });
        onSave(); // go back to insurance lists

        if (!userID) return;
        const { success } = await dispatch(updateCashPay(userID, false));
        if (!success) return;
        trackEvent({
          event: EVENTS.CASH_PAY_ONLY_SET,
          params: {
            source,
            'new value': false,
          },
        });
      }
    }
  };

  const { setValue, getValues } = useForm({ defaultValues: initialValues });

  const missingPhotoError = frontPhotoError || backPhotoError;
  const handleFormChange = (name: 'image_url' | 'image_url_2', files: File[]) => {
    const hasImage = files.length > 0;
    if (name === 'image_url' && hasImage && frontPhotoError) setFrontPhotoError('');
    if (name === 'image_url_2' && hasImage && backPhotoError) setBackPhotoError('');
    setValue(name, files);
  };

  return (
    <LgPadding>
      <Column gap="LG">
        <Body>Upload a photo of the front and back of your prescription benefit card.</Body>
        <InsurancePhotoUploadButtons
          loading={loading}
          onFormChange={handleFormChange}
          initialFrontImageUrl={''}
          initialBackImageUrl={''}
        />
        {missingPhotoError ? (
          <InlineAlert type="error">
            <Body>{missingPhotoError}</Body>
          </InlineAlert>
        ) : null}
        {insuranceError ? (
          <InlineAlert type="error">
            <Body fontFamily="semibold">{DEFAULT_ERROR_ALERT_TITLE}</Body>
            <Body>There was an error uploading your benefit card. {DEFAULT_ERROR_ALERT_MESSAGE}</Body>
          </InlineAlert>
        ) : null}
        <ButtonGroup
          justifyButtons="right"
          inline
          buttons={[
            <Button
              key="cancel-button"
              label="Cancel"
              accessibilityLabel="Press to close photo upload form"
              type="tertiary"
              onPress={onCancel}
            />,
            <Button
              key="add-benefits-button"
              label="Add benefits"
              accessibilityLabel="Press to add benefits"
              loading={loading}
              loadingLabel="Adding benefits"
              onPress={() => {
                onSubmit(getValues());
              }}
            />,
          ]}
        />
      </Column>
    </LgPadding>
  );
};
